/* ============================================================================
   Slide 4 — "What are Dreams?" — SHARED CONTENT (single source of truth)
   Read by both HTLDSlide4Inner (desktop) and HTLDSlide4Mobile. Edit copy ONLY
   here — English and German side by side; the German object must have exactly
   the same shape as the English one.
   ========================================================================== */
import { ByLang, useL } from "i18n/lang";

export const accent4 = "#d4537e";

/** A rich-text segment: plain, bold, or emphasised. */
export interface Seg { t: string; b?: boolean; em?: boolean; }

export interface DreamFact { num: string; title: string; body: string; }

const en = {
  meta: {
    breadcrumbPre: "Understanding Dreams",
    breadcrumbAccent: "What are dreams?",
    headerPlain: "The Dream",
    headerAccent: "State",
    titlePlain: "What are",
    titleAccent: "Dreams?",
    subtitle: "Dreams are not random — they are the subconscious mind thinking out loud",
    whatLabel: "What actually happens",
    perspectiveLabel: "The Dreamfulness perspective",
  },
  facts: [
    { num: "01", title: "The logical mind steps back",         body: "During REM sleep, the prefrontal cortex — seat of logic and critical thinking — quiets. The subconscious takes the wheel." },
    { num: "02", title: "The brain generates its own reality", body: "With no external sensory input, the brain constructs scenes, emotions, and narratives entirely from memory and feeling." },
    { num: "03", title: "The body believes it is real",        body: "The same neural pathways fire whether dreaming or awake. Emotions and physical responses react as if it is happening now." },
  ] as DreamFact[],
  perspective: [
    { t: "If the dreaming brain cannot tell the difference between a dream and reality, then a wellness experience inside a lucid dream is — " },
    { t: "to the nervous system, a real wellness experience.", b: true },
    { t: " This is the foundation Dreamfulness is built on." },
  ] as Seg[],
  canvas: {
    rem: "REM STATE",
    dreaming: "dreaming…",
  },
};

export type Slide4Content = typeof en;

const de: Slide4Content = {
  meta: {
    breadcrumbPre: "Träume verstehen",
    breadcrumbAccent: "Was sind Träume?",
    headerPlain: "Der",
    headerAccent: "Traumzustand",
    titlePlain: "Was sind",
    titleAccent: "Träume?",
    subtitle: "Träume sind kein Zufall – in ihnen denkt das Unterbewusstsein laut",
    whatLabel: "Was wirklich passiert",
    perspectiveLabel: "Die Sicht von Dreamfulness",
  },
  facts: [
    { num: "01", title: "Der logische Verstand tritt zurück",        body: "Im REM-Schlaf wird der präfrontale Kortex – Sitz von Logik und kritischem Denken – still. Das Unterbewusstsein übernimmt das Steuer." },
    { num: "02", title: "Das Gehirn erschafft seine eigene Realität", body: "Ohne äußere Sinnesreize baut das Gehirn Szenen, Gefühle und Geschichten ganz aus Erinnerung und Empfinden." },
    { num: "03", title: "Der Körper hält es für echt",               body: "Im Traum feuern dieselben Nervenbahnen wie im Wachzustand. Gefühle und Körper reagieren, als geschähe es genau jetzt." },
  ],
  perspective: [
    { t: "Wenn das träumende Gehirn Traum und Wirklichkeit nicht unterscheiden kann, dann ist ein Wellness-Erlebnis in einem luziden Traum – " },
    { t: "für das Nervensystem ein echtes Wellness-Erlebnis.", b: true },
    { t: " Genau darauf baut Dreamfulness auf." },
  ],
  canvas: {
    rem: "REM-ZUSTAND",
    dreaming: "träumt…",
  },
};

export const slide4Content: ByLang<Slide4Content> = { en, de };
export const useSlide4 = () => useL(slide4Content);
